import React, { useState, useEffect } from "react";
import AuthHeader from "../../../UI/AuthHeader/AuthHeader";
import AuthSideMenu from "../../../UI/AuthSideMenu/AuthSideMenu";
import { useNavigate } from "react-router-dom";
import { useDispatch } from 'react-redux';
import { Modal } from "../../../UI/Modal/Modal";
import Rules from "../../Rules";
import BubbleFloat from "../../../UI/BubbleFloat/BubbleFloat";
import { checkAuthenticatedUser, startExamSession } from "../../../../utils/client-functions";
import { useErrorSetter } from '../../../../hooks/useUiMessages';
import useExamStartGate from "../../../../hooks/useExamStartGate";
import ExamReadyModal from "../../ExamReadyModal";

// NSCDC Recruitment CBT mirrors NCS single combined exam flow

const Nscdc = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [authMenuOpen, setAuthMenuOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [progressStatus, setProgressStatus] = useState('Gathering Exam Details...');
  const [progressPercentage, setProgressPercentage] = useState(0);
  const { examReady, handleOngoing, handleDoneEvent, confirmStartExam, closeGate } = useExamStartGate(navigate, dispatch);

  const isAuth = localStorage?.getItem("token");
  const errorSetter = useErrorSetter();

  const toggleAuthMenu = () => setAuthMenuOpen(!authMenuOpen);

    useEffect(() => {
        const verifyAuth = async () => {
            try {
                await checkAuthenticatedUser();
            } catch (error) {
                errorSetter("Please login to access mock exams");
                sessionStorage.setItem("prevPath", window.location.pathname);
                navigate("/popin");
            }
        };
        verifyAuth();
    }, [navigate, errorSetter]);

  // Single "subject" metadata used by Rules and backend
  const nscdcExamMeta = {
    subject: "Nigeria Security and Civil Defence Corps (NSCDC) Recruitment CBT",
    timeAlloted: 2700,
    description: `This mock simulates the NSCDC Recruitment Computer-Based Test (CBT) to help you practice under realistic conditions.\n\nConduct Rules (practice simulation):\n- Online CBT with identity/webcam verification.\n- Use a laptop/desktop (no mobile devices).\n- Maintain single-window focus; malpractice may lead to termination.\n- A short practice reminder may precede the main exam.\n\nBlueprint (practice approximation):\n- 50 MCQs across: General Knowledge & Current Affairs, General Mathematics, English Language & Verbal Reasoning.\n- Questions balanced by difficulty with timed conditions.\n\nDisclaimer: This mock is an informed approximation for training speed, accuracy, and exam discipline.`,
  };

  const closeModalsAndReset = () => {
    navigate('/user/dashboard/mock');
  };

  const handleStartExam = async () => {
    try {
      await checkAuthenticatedUser();
    } catch (error) {
      errorSetter("Please login to generate an exam");
            sessionStorage.setItem("prevPath", window.location.pathname);
            return navigate("/popin");
    }

    setLoading(true);
    setProgressStatus("Gathering Exam Details...");
    setProgressPercentage(5);

    const payload = {
      subject: nscdcExamMeta.subject,
      timeAlloted: nscdcExamMeta.timeAlloted,
      description: nscdcExamMeta.description,
      examName: "NSCDC",
    };

    try {
      await startExamSession({
        payload,
        tokenOverride: isAuth,
        onOngoing: (examDetails) => { setLoading(false); handleOngoing(examDetails); },
        onDone: (e) => { handleDoneEvent(e); },
        onError: (msg) => { setLoading(false); errorSetter(msg); },
        setLoading, setProgressStatus, setProgressPercentage
      });
    } catch (error) {
      setLoading(false);
      errorSetter(error?.response?.data?.message || error?.response?.data?.error || "An Error Occurred. Please try again. If issue persists, please chat with us.");
      if (error?.response?.data?.openPricing) {
        window.dispatchEvent(new CustomEvent('openExamPricing'));
      }
    }
  };

  return (
    <div className="auth-container">
      <BubbleFloat />
      <div className="auth-container-inner content">
        <AuthHeader
          authMenuOpen={authMenuOpen}
          onClick={toggleAuthMenu}
          headerText="NSCDC MOCK"
        />
        <AuthSideMenu opened={authMenuOpen} arrayDetails={[]} onSwitch={() => {}} />
      </div>

      {!examReady && (
        <Rules
          examName={"Nigeria Security and Civil Defence Corps Recruitment CBT"}
          closeModalsAndReset={closeModalsAndReset}
          subject={nscdcExamMeta}
          handleStartExam={handleStartExam}
        />
      )}

      {loading && (
        <Modal
          header4={`PROCESSING... DO NOT RELOAD`}
          header3={progressStatus}
          progress={progressPercentage}
        />
      )}
      {examReady && (
        <ExamReadyModal
          isOpen={examReady}
          onClose={closeGate}
          onStart={confirmStartExam}
          examLabel="NSCDC"
        />
      )}
    </div>
  );
};

export default Nscdc;

